"use client"

import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	DragOverlay,
	type DragStartEvent,
	type DropAnimation,
	defaultDropAnimationSideEffects,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors
} from "@dnd-kit/core"
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import * as React from "react"
import { cn } from "../../lib/utils"
import type { DisplayChoice, DisplayOrderInteraction } from "../../shared/types"
import { HTMLContent } from "../shared/html-content"

// --- Drag Handle Icon ---
function DragHandleIcon({ className }: { className?: string }) {
	return (
		<svg
			width="12"
			height="12"
			viewBox="0 0 15 15"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
			aria-hidden="true"
			role="img"
		>
			<title>Drag handle</title>
			<path
				d="M5.5 3C5.5 3.27614 5.27614 3.5 5 3.5C4.72386 3.5 4.5 3.27614 4.5 3C4.5 2.72386 4.72386 2.5 5 2.5C5.27614 2.5 5.5 2.72386 5.5 3ZM5.5 7.5C5.5 7.77614 5.27614 8 5 8C4.72386 8 4.5 7.77614 4.5 7.5C4.5 7.22386 4.72386 7 5 7C5.27614 7 5.5 7.22386 5.5 7.5ZM5.5 12C5.5 12.2761 5.27614 12.5 5 12.5C4.72386 12.5 4.5 12.2761 4.5 12C4.5 11.7239 4.72386 11.5 5 11.5C5.27614 11.5 5.5 11.7239 5.5 12ZM9.5 3C9.5 3.27614 9.27614 3.5 9 3.5C8.72386 3.5 8.5 3.27614 8.5 3C8.5 2.72386 8.72386 2.5 9 2.5C9.27614 2.5 9.5 2.72386 9.5 3ZM9.5 7.5C9.5 7.77614 9.27614 8 9 8C8.72386 8 8.5 7.77614 8.5 7.5C8.5 7.22386 8.72386 7 9 7C9.27614 7 9.5 7.22386 9.5 7.5ZM9.5 12C9.5 12.2761 9.27614 12.5 9 12.5C8.72386 12.5 8.5 12.2761 8.5 12C8.5 11.7239 8.72386 11.5 9 11.5C9.27614 11.5 9.5 11.7239 9.5 12Z"
				fill="currentColor"
				fillRule="evenodd"
				clipRule="evenodd"
			/>
		</svg>
	)
}

// --- Sortable Item ---
function SortableItem({
	id,
	contentHtml,
	disabled,
	isHorizontal
}: {
	id: string
	contentHtml: string
	disabled: boolean
	isHorizontal: boolean
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.4 : 1,
		zIndex: isDragging ? 50 : ("auto" as const)
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			data-disabled={disabled}
			data-dragging={isDragging}
			className={cn(
				"qti-order-item",
				// Styling differs based on orientation
				isHorizontal ? "min-w-[100px] flex items-center justify-center text-center" : "w-full flex items-center gap-3",
				disabled ? "cursor-default" : "cursor-grab active:cursor-grabbing"
			)}
		>
			{!isHorizontal && (
				<div className="qti-order-item-handle shrink-0">
					<DragHandleIcon />
				</div>
			)}
			<HTMLContent html={contentHtml} />
		</div>
	)
}

// --- Drag Overlay Item ---
function DragOverlayItem({ contentHtml, isHorizontal }: { contentHtml: string; isHorizontal: boolean }) {
	return (
		<div
			data-dragging="true"
			className={cn(
				"qti-order-item cursor-grabbing z-50",
				isHorizontal ? "min-w-[100px] flex items-center justify-center" : "w-full flex items-center gap-3"
			)}
		>
			{!isHorizontal && (
				<div className="qti-order-item-handle shrink-0">
					<DragHandleIcon />
				</div>
			)}
			<HTMLContent html={contentHtml} />
		</div>
	)
}

// --- Main Component ---
export interface OrderInteractionProps {
	interaction: DisplayOrderInteraction
	response?: string[]
	onAnswerSelect?: (value: string[]) => void
	disabled?: boolean
	hasSubmitted?: boolean
	isCorrect?: boolean
}

export function OrderInteraction({
	interaction,
	response,
	onAnswerSelect,
	disabled,
	hasSubmitted,
	isCorrect
}: OrderInteractionProps) {
	// Use response if available (user state), otherwise fallback to initial shuffled order
	const items = React.useMemo(() => {
		if (response && response.length > 0) {
			// Re-order the choices array based on the response ID list
			const mapped: DisplayChoice[] = []
			for (const id of response) {
				const found = interaction.choices.find((c) => c.id === id)
				if (found) mapped.push(found)
			}
			return mapped
		}
		return interaction.choices
	}, [response, interaction.choices])

	// Local state for active drag item (for overlay)
	const [activeId, setActiveId] = React.useState<string | null>(null)
	const activeItem = React.useMemo(() => items.find((i) => i.id === activeId), [activeId, items])

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	)

	const handleDragStart = (event: DragStartEvent) => {
		const id = event.active.id
		setActiveId(typeof id === "string" ? id : String(id))
	}

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		setActiveId(null)

		if (over && active.id !== over.id) {
			const oldIndex = items.findIndex((item) => item.id === active.id)
			const newIndex = items.findIndex((item) => item.id === over.id)

			const newOrder = arrayMove(items, oldIndex, newIndex)

			// Notify parent
			onAnswerSelect?.(newOrder.map((item) => item.id))
		}
	}

	const isHorizontal = interaction.orientation === "horizontal"
	const strategy = isHorizontal ? horizontalListSortingStrategy : verticalListSortingStrategy

	// Smooth drop animation configuration
	const dropAnimation: DropAnimation = {
		sideEffects: defaultDropAnimationSideEffects({
			styles: { active: { opacity: "0.4" } }
		})
	}

	// Determine correctness attribute for container
	const getContainerCorrectness = () => {
		if (!hasSubmitted) return undefined
		return isCorrect
	}

	return (
		<div className="space-y-4">
			{interaction.promptHtml && <HTMLContent html={interaction.promptHtml} />}

			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
			>
				<SortableContext items={items.map((i) => i.id)} strategy={strategy} disabled={disabled || hasSubmitted}>
					<div
						data-correct={getContainerCorrectness()}
						className={cn("qti-order-container", isHorizontal ? "flex flex-wrap" : "flex flex-col")}
					>
						{items.map((item) => (
							<SortableItem
								key={item.id}
								id={item.id}
								contentHtml={item.contentHtml}
								disabled={!!(disabled || hasSubmitted)}
								isHorizontal={isHorizontal}
							/>
						))}
					</div>
				</SortableContext>

				{/* Portal Overlay for smooth visual while dragging */}
				<DragOverlay dropAnimation={dropAnimation}>
					{activeItem ? <DragOverlayItem contentHtml={activeItem.contentHtml} isHorizontal={isHorizontal} /> : null}
				</DragOverlay>
			</DndContext>
		</div>
	)
}

export default OrderInteraction
